import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Copy recurring inspections from one project into selected (or all) target projects.
export default function CopyInspectionsDialog({ open, onOpenChange, onCopied }) {
  const [projects, setProjects] = useState([]);
  const [source, setSource] = useState("");
  const [targets, setTargets] = useState([]);
  const [includeMaintenance, setIncludeMaintenance] = useState(true);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    setSource(""); setTargets([]);
  }, [open]);

  const others = projects.filter((p) => p.id !== source);
  const allSelected = others.length > 0 && targets.length === others.length;
  const toggleAll = () => setTargets(allSelected ? [] : others.map((p) => p.id));
  const toggle = (id) => setTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const run = async () => {
    if (!source) return toast.error("Bitte Quell-Projekt wählen");
    if (!targets.length) return toast.error("Bitte mindestens ein Ziel-Projekt wählen");
    setBusy(true);
    try {
      const res = await api.post("/inspections/copy", {
        source_project_id: source, target_project_ids: targets, include_maintenance: includeMaintenance,
      });
      const s = res.data;
      toast.success(`${s.created} Prüfung(en) kopiert${s.skipped ? `, ${s.skipped} übersprungen (bereits vorhanden)` : ""}`);
      onCopied?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kopieren fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="copy-inspections-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-primary" />Wiederkehrende Prüfungen kopieren</DialogTitle>
          <DialogDescription>
            Kopiert die wiederkehrenden Prüfungen eines Projekts als Vorlage (ohne Datum) in die gewählten Projekte. Bereits vorhandene Titel werden übersprungen.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="text-xs text-slate-500">Quelle (z. B. „Zentrum")</label>
          <Select value={source} onValueChange={(v) => { setSource(v); setTargets([]); }}>
            <SelectTrigger data-testid="copy-source-select"><SelectValue placeholder="Quell-Projekt wählen…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {source && (
          <div className="mt-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-slate-500">Ziel-Projekte</label>
              <button onClick={toggleAll} className="text-xs text-primary hover:underline" data-testid="copy-toggle-all">
                {allSelected ? "Alle abwählen" : "Alle auswählen"}
              </button>
            </div>
            <div className="mt-1 max-h-[40vh] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="copy-targets">
              {others.length === 0 && <p className="p-3 text-sm text-slate-400">Keine weiteren Projekte vorhanden.</p>}
              {others.map((p) => (
                <label key={p.id} className="flex items-center gap-2 p-2.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`copy-target-${p.id}`}>
                  <Checkbox checked={targets.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="text-slate-800">{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <Checkbox checked={includeMaintenance} onCheckedChange={(v) => setIncludeMaintenance(!!v)} data-testid="copy-include-maintenance" />
          Wiederkehrende Wartungen ebenfalls kopieren
        </label>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="copy-cancel-btn">Abbrechen</Button>
          <Button onClick={run} disabled={busy || !source || !targets.length} data-testid="copy-start-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}Kopieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
